import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Checkbox, FormControlLabel, Chip, CircularProgress
} from '@material-ui/core'
import {
  NOTE_KIND_LABELS, NOTE_VISIBILITY_LABELS,
  BLOCKER_CATEGORY_LABELS,
  DECISION_TYPE_LABELS,
  REVIEW_EVENT_TYPE_LABELS,
  CONVERSATION_TYPE_LABELS,
  TASK_TYPE_LABELS,
  PRIORITY_LABELS,
  PARENT_TYPE_LABELS
} from '../../constants/contextEnums'

const MODE_OPTIONS = [
  { key: 'note', label: 'Note' },
  { key: 'task', label: 'Task' },
  { key: 'blocker', label: 'Blocker' },
  { key: 'decision', label: 'Decision' },
  { key: 'review_event', label: 'Review' },
  { key: 'conversation', label: 'Conversation' }
]

const ContextCreateModal = ({ open, onClose, onSaved, projectId, mode: initialMode, parentContext, editRecord, url }) => {
  const [mode, setMode] = React.useState(initialMode || 'note')
  const [saving, setSaving] = React.useState(false)
  const [form, setForm] = React.useState({})
  const [error, setError] = React.useState('')

  const isEdit = !!editRecord

  // Reset form when modal opens or mode/editRecord changes
  React.useEffect(() => {
    if (!open) return
    setMode(initialMode || 'note')
    setError('')
    if (editRecord) {
      setForm({ ...editRecord })
    } else {
      const defaults = {
        title: '',
        body: '',
        description: '',
        kind: 'general',
        priority: 'normal',
        visibility: 'internal',
        category: 'other',
        waiting_on_name: '',
        expected_response_at: '',
        blocks_output: false,
        blocks_recalculate: false,
        decision: '',
        rationale: '',
        decision_type: 'other',
        affects_price: false,
        affects_output: false,
        event_type: 'internal_review',
        starts_at: '',
        ends_at: '',
        location: '',
        agenda: '',
        interaction_type: 'call',
        contact_name: '',
        occurred_at: new Date().toISOString().slice(0, 16),
        task_type: 'other',
        dueDate: '',
        parent_type: parentContext?.parent_type || '',
        parent_id: parentContext?.parent_id || '',
        parent_label: parentContext?.parent_label || ''
      }
      setForm(defaults)
    }
  }, [open, initialMode, editRecord])

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    if (!form.title || !form.title.trim()) {
      setError('Title is required')
      return
    }
    setError('')

    setSaving(true)

    const endpointMap = {
      note: { create: `${url}/api/bid-estimation/projects/${projectId}/notes`, update: `${url}/api/bid-estimation/notes` },
      task: { create: `${url}/api/bid-estimation/projects/${projectId}/tasks`, update: `${url}/api/bid-estimation/tasks` },
      blocker: { create: `${url}/api/bid-estimation/projects/${projectId}/blockers`, update: `${url}/api/bid-estimation/blockers` },
      decision: { create: `${url}/api/bid-estimation/projects/${projectId}/decisions`, update: `${url}/api/bid-estimation/decisions` },
      review_event: { create: `${url}/api/bid-estimation/projects/${projectId}/review-events`, update: `${url}/api/bid-estimation/review-events` },
      conversation: { create: `${url}/api/bid-estimation/projects/${projectId}/conversations`, update: `${url}/api/bid-estimation/conversations` }
    }

    const ep = endpointMap[mode]
    if (!ep) { setSaving(false); return }

    const request = isEdit
      ? axios.put(`${ep.update}/${editRecord._id}`, form)
      : axios.post(ep.create, form)

    request.then(() => {
      setSaving(false)
      if (onSaved) onSaved()
    }).catch(err => {
      setSaving(false)
      console.error(`Failed to save ${mode}:`, err)
      setError('Failed to save: ' + (err.response?.data?.message || err.message))
    })
  }

  const renderSelect = (label, field, options) => (
    <FormControl fullWidth size="small" margin="dense">
      <InputLabel>{label}</InputLabel>
      <Select value={form[field] || ''} onChange={e => update(field, e.target.value)} label={label}>
        {Object.entries(options).map(([key, lbl]) => (
          <MenuItem key={key} value={key}>{lbl}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  const renderFields = () => {
    switch (mode) {
      case 'note':
        return (
          <>
            <TextField label="Title" required fullWidth size="small" value={form.title || ''} onChange={e => { update('title', e.target.value); setError('') }} margin="dense" autoFocus inputProps={{ maxLength: 300 }} error={!!error} helperText={error} />
            <TextField label="Body" fullWidth size="small" multiline rows={4} value={form.body || ''} onChange={e => update('body', e.target.value)} margin="dense" />
            {renderSelect('Kind', 'kind', NOTE_KIND_LABELS)}
            {renderSelect('Priority', 'priority', PRIORITY_LABELS)}
            {renderSelect('Visibility', 'visibility', NOTE_VISIBILITY_LABELS)}
          </>
        )

      case 'task':
        return (
          <>
            <TextField label="Title" required fullWidth size="small" value={form.title || ''} onChange={e => { update('title', e.target.value); setError('') }} margin="dense" autoFocus inputProps={{ maxLength: 300 }} error={!!error} helperText={error} />
            <TextField label="Description" fullWidth size="small" multiline rows={4} value={form.description || ''} onChange={e => update('description', e.target.value)} margin="dense" />
            {renderSelect('Task Type', 'task_type', TASK_TYPE_LABELS)}
            {renderSelect('Priority', 'priority', PRIORITY_LABELS)}
            <TextField
              label="Due Date"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.dueDate ? form.dueDate.slice(0, 10) : ''}
              onChange={e => update('dueDate', e.target.value)}
              margin="dense"
            />
          </>
        )

      case 'blocker':
        return (
          <>
            <TextField label="Title" required fullWidth size="small" value={form.title || ''} onChange={e => { update('title', e.target.value); setError('') }} margin="dense" autoFocus inputProps={{ maxLength: 300 }} error={!!error} helperText={error} />
            <TextField label="Body" fullWidth size="small" multiline rows={4} value={form.body || ''} onChange={e => update('body', e.target.value)} margin="dense" />
            {renderSelect('Category', 'category', BLOCKER_CATEGORY_LABELS)}
            {renderSelect('Priority', 'priority', PRIORITY_LABELS)}
            <TextField label="Waiting On" fullWidth size="small" value={form.waiting_on_name || ''} onChange={e => update('waiting_on_name', e.target.value)} margin="dense" />
            <TextField
              label="Expected Response"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.expected_response_at ? form.expected_response_at.slice(0, 10) : ''}
              onChange={e => update('expected_response_at', e.target.value)}
              margin="dense"
            />
            <FormControlLabel
              control={<Checkbox checked={!!form.blocks_output} onChange={e => update('blocks_output', e.target.checked)} size="small" />}
              label="Blocks output"
              style={{ marginBottom: 4 }}
            />
            <FormControlLabel
              control={<Checkbox checked={!!form.blocks_recalculate} onChange={e => update('blocks_recalculate', e.target.checked)} size="small" />}
              label="Blocks recalculate"
              style={{ marginBottom: 8 }}
            />
          </>
        )

      case 'decision':
        return (
          <>
            <TextField label="Title" required fullWidth size="small" value={form.title || ''} onChange={e => { update('title', e.target.value); setError('') }} margin="dense" autoFocus inputProps={{ maxLength: 300 }} error={!!error} helperText={error} />
            <TextField label="Decision" required fullWidth size="small" multiline rows={3} value={form.decision || ''} onChange={e => update('decision', e.target.value)} margin="dense" />
            <TextField label="Rationale" fullWidth size="small" multiline rows={3} value={form.rationale || ''} onChange={e => update('rationale', e.target.value)} margin="dense" />
            {renderSelect('Decision Type', 'decision_type', DECISION_TYPE_LABELS)}
            <FormControlLabel
              control={<Checkbox checked={!!form.affects_price} onChange={e => update('affects_price', e.target.checked)} size="small" />}
              label="Affects price"
              style={{ marginBottom: 4 }}
            />
            <FormControlLabel
              control={<Checkbox checked={!!form.affects_output} onChange={e => update('affects_output', e.target.checked)} size="small" />}
              label="Affects output"
              style={{ marginBottom: 8 }}
            />
          </>
        )

      case 'review_event':
        return (
          <>
            <TextField label="Title" required fullWidth size="small" value={form.title || ''} onChange={e => { update('title', e.target.value); setError('') }} margin="dense" autoFocus inputProps={{ maxLength: 300 }} error={!!error} helperText={error} />
            {renderSelect('Event Type', 'event_type', REVIEW_EVENT_TYPE_LABELS)}
            <TextField
              label="Starts At"
              type="datetime-local"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.starts_at ? form.starts_at.slice(0, 16) : ''}
              onChange={e => update('starts_at', e.target.value)}
              margin="dense"
            />
            <TextField
              label="Ends At"
              type="datetime-local"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.ends_at ? form.ends_at.slice(0, 16) : ''}
              onChange={e => update('ends_at', e.target.value)}
              margin="dense"
            />
            <TextField label="Location" fullWidth size="small" value={form.location || ''} onChange={e => update('location', e.target.value)} margin="dense" />
            <TextField label="Agenda" fullWidth size="small" multiline rows={3} value={form.agenda || ''} onChange={e => update('agenda', e.target.value)} margin="dense" />
          </>
        )

      case 'conversation':
        return (
          <>
            <TextField label="Title" required fullWidth size="small" value={form.title || ''} onChange={e => { update('title', e.target.value); setError('') }} margin="dense" autoFocus inputProps={{ maxLength: 300 }} error={!!error} helperText={error} />
            <TextField label="Body" fullWidth size="small" multiline rows={4} value={form.body || ''} onChange={e => update('body', e.target.value)} margin="dense" />
            {renderSelect('Interaction Type', 'interaction_type', CONVERSATION_TYPE_LABELS)}
            <TextField label="Contact Name" fullWidth size="small" value={form.contact_name || ''} onChange={e => update('contact_name', e.target.value)} margin="dense" />
            <TextField
              label="Occurred At"
              type="datetime-local"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.occurred_at ? form.occurred_at.slice(0, 16) : ''}
              onChange={e => update('occurred_at', e.target.value)}
              margin="dense"
            />
          </>
        )

      default:
        return null
    }
  }

  const modeLabel = MODE_OPTIONS.find(m => m.key === mode)?.label || mode

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? `Edit ${modeLabel}` : `Create ${modeLabel}`}</DialogTitle>
      <DialogContent>
        {/* Mode selector — only in create mode */}
        {!isEdit && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {MODE_OPTIONS.map(opt => (
              <Chip
                key={opt.key}
                label={opt.label}
                clickable
                color={mode === opt.key ? 'primary' : 'default'}
                variant={mode === opt.key ? 'default' : 'outlined'}
                size="small"
                onClick={() => setMode(opt.key)}
              />
            ))}
          </div>
        )}

        {renderFields()}

        {/* Parent context fields */}
        <div style={{ borderTop: '1px solid var(--line, #e0e0e0)', paddingTop: 12, marginTop: 8 }}>
          {renderSelect('Parent Type', 'parent_type', PARENT_TYPE_LABELS)}
          <TextField label="Parent Label" fullWidth size="small" value={form.parent_label || ''} onChange={e => update('parent_label', e.target.value)} margin="dense" />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={saving || !form.title}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(ContextCreateModal)
